import type { FC } from 'react';
import { Menu, MenuItem } from '@mui/material';
import { ValidationDismissalReason } from '../../../types/validation-types';

interface ValidationErrorInfo {
  id: number;
  field: string;
  error?: string;
  errorDetail?: string;
  extractionId?: string;
  dismissalReason?: ValidationDismissalReason | null;
}

export type TransformedGridContextMenuPosition = {
  mouseX: number | null;
  mouseY: number | null;
};

type TransformedGridContextMenuProps = {
  open: boolean;
  position: TransformedGridContextMenuPosition;
  cellValue?: any;
  validationError: ValidationErrorInfo | null;
  onClose: () => void;
  onDismissError: () => void;
  gridApi?: any;
};

const TransformedGridContextMenu: FC<TransformedGridContextMenuProps> = ({
  open,
  position,
  cellValue,
  validationError,
  onClose,
  onDismissError,
  gridApi,
}) => {
  const copyText = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (_) {
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
  };

  const formatClipboard = (val: any): string => {
    if (val == null) return '';
    if (typeof val === 'object') {
      try {
        return JSON.stringify(val);
      } catch {
        return String(val);
      }
    }
    return String(val);
  };

  const handleCopy = () => {
    if (cellValue != null) {
      copyText(formatClipboard(cellValue));
      onClose();
      return;
    }
    const focused = gridApi?.getFocusedCell?.();
    if (focused) {
      const rowNode = gridApi?.getDisplayedRowAtIndex?.(focused.rowIndex);
      const val = rowNode?.data?.[focused.column?.getColId?.() ?? ''];
      if (val != null) copyText(formatClipboard(val));
    }
    onClose();
  };

  const dismissLabel = validationError?.dismissalReason
    ? `Error Dismissed (${validationError.dismissalReason})`
    : 'Dismiss Error';

  return (
    <Menu
      open={open}
      onClose={onClose}
      anchorReference="anchorPosition"
      anchorPosition={
        position.mouseY !== null && position.mouseX !== null
          ? { top: position.mouseY, left: position.mouseX }
          : undefined
      }
      data-cy="transformed-grid-context-menu"
      PaperProps={{
        sx: {
          minWidth: 120,
          '& .MuiMenuItem-root': {
            fontSize: 12,
            lineHeight: 1.2,
            minHeight: 28,
            py: 0.5,
            px: 1,
          },
          '& .MuiList-root': {
            py: 0.25,
          },
        },
        elevation: 3,
      }}
    >
      <MenuItem onClick={handleCopy} data-cy="ctx-menu-copy">
        Copy
      </MenuItem>
      {validationError && (
        <MenuItem onClick={onDismissError} data-cy="ctx-menu-dismiss-error">
          {dismissLabel}
        </MenuItem>
      )}
    </Menu>
  );
};

export default TransformedGridContextMenu;
